import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { STANDING_ALIGNMENT } from '@erectus/shared';
import type { AlignmentGrade, PostureCheck } from '@erectus/shared';
import { AccuracyNote, Button, Card, SectionTitle } from '@/ui/primitives.js';
import { PageHeader } from '@/ui/Layout.js';
import { usesImperial } from '@/lib/units.js';

const GRADE: Record<AlignmentGrade, { label: string; color: string }> = {
  aligned: { label: 'Aligned', color: 'var(--color-state-good)' },
  slightlyForward: {
    label: 'A little forward',
    color: 'var(--color-state-fhp)',
  },
  forward: { label: 'Forward', color: 'var(--color-state-slouch)' },
  leaningBack: { label: 'Leaning back', color: 'var(--color-state-fhp)' },
};

function length(cm: number): string {
  return usesImperial() ? `${(cm / 2.54).toFixed(1)} in` : `${cm.toFixed(1)} cm`;
}

function pm(value: number | null, sd: number | null, unit: string, digits = 1): string {
  if (value === null) return '—';
  return `${value.toFixed(digits)}${unit}${sd !== null ? ` ± ${sd.toFixed(digits)}` : ''}`;
}

/** The result of one posture check, with the numbers behind the grades. */
export function CheckResult({
  check,
  note,
  onAgain,
}: {
  check: PostureCheck;
  note?: string | null;
  onAgain?(): void;
}): ReactNode {
  const s = check.standing;
  return (
    // The check screen has no app shell (no tab bar, no scroller), so the
    // result brings its own full-screen scroller.
    <div className="fixed inset-0 overflow-y-auto bg-surface">
      <div className="mx-auto max-w-lg space-y-4 px-4 pt-[calc(1rem+env(safe-area-inset-top))] pb-[calc(1rem+env(safe-area-inset-bottom))]">
        <PageHeader
          title={check.kind === 'seated' ? 'Sitting baseline' : 'Standing alignment'}
          subtitle={new Date(check.at).toLocaleString()}
        />
        {note ? <p className="text-sm text-[color:var(--color-state-fhp)]">{note}</p> : null}

        {check.seated ? (
          <Card className="space-y-2">
            <SectionTitle>Sitting tall — your baseline</SectionTitle>
            <dl className="grid grid-cols-2 gap-2 text-sm">
              <dt className="text-muted">Neck angle</dt>
              <dd className="font-semibold">{pm(check.seated.cva ?? null, check.seated.cvaSd ?? null, '°')}</dd>
              <dt className="text-muted">Trunk lean</dt>
              <dd className="font-semibold">{pm(check.seated.trunk, check.seated.trunkSd, '°')}</dd>
            </dl>
            <p className="text-xs text-muted">
              Desk sessions now compare you with this. ± is how much it moved during the five seconds.
            </p>
          </Card>
        ) : null}

        {s ? (
          <Card className="space-y-3">
            <SectionTitle>Standing side-on</SectionTitle>
            <ul className="space-y-2">
              {STANDING_ALIGNMENT.measures.map((measure) => {
                const grade = s.grades[measure.id];
                if (!grade) return null;
                const deg = measure.id === 'earLine' ? s.bodyLineDeg : s.shoulderLineDeg;
                const spread = measure.id === 'earLine' ? s.bodyLineSd : s.shoulderLineSd;
                const cm = measure.id === 'earLine' ? s.earOffsetCm : s.shoulderOffsetCm;
                return (
                  <li key={measure.id} className="flex items-start justify-between gap-3 text-sm">
                    <span>
                      <span className="block font-medium">{measure.label}</span>
                      <span className="block text-xs text-muted">
                        {pm(deg, spread, '° forward')}
                        {cm !== null ? ` · ${length(cm)} ahead of the ankle` : ''}
                      </span>
                    </span>
                    <span className="shrink-0 font-semibold" style={{ color: GRADE[grade].color }}>
                      {GRADE[grade].label}
                    </span>
                  </li>
                );
              })}
            </ul>
            <p className="text-xs text-muted">
              Healthy adults stand with the ankle-to-ear line about 2° forward.{' '}
              <Link to="/sources" className="underline">
                Sources
              </Link>
            </p>
          </Card>
        ) : null}

        <Card className="space-y-1 text-xs text-muted">
          <p>
            {check.sampleCount} readings over {check.durationMs / 1000} s ·{' '}
            {check.facing === 'user' ? 'front' : 'rear'} camera
            {check.phoneRollDeg !== null
              ? ` · phone ${Math.abs(check.phoneRollDeg).toFixed(1)}° sideways (corrected), ${check.phonePitchDeg?.toFixed(0) ?? '?'}° leaning`
              : ' · phone tilt not available'}
          </p>
          <AccuracyNote />
        </Card>

        <div className="grid gap-2">
          {onAgain ? (
            <Button variant="secondary" onClick={onAgain}>
              Measure again
            </Button>
          ) : null}
          <Link
            to="/progress"
            replace
            className="rounded-xl bg-ink px-4 py-3 text-center text-sm font-semibold text-white"
          >
            Done
          </Link>
        </div>
      </div>
    </div>
  );
}
